import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

import { MenuPanelItemModel } from './menu-panel-item.model';

const MENU_ANCHOR_OFFSET_PX = 4;
const MENU_VIEWPORT_MARGIN_PX = 8;

@Component({
  selector: 'rnpl-menu-panel',
  templateUrl: './menu-panel.component.html',
  styleUrls: ['./menu-panel.component.scss'],
})
export class MenuPanelComponent implements OnChanges, OnDestroy {

  @Input() items: MenuPanelItemModel[] = [];
  @Input() closeOnSelect = true;
  @Input() panelClass = '';
  @Input() xPosition: 'before' | 'after' = 'before';
  @Input() yPosition: 'above' | 'below' = 'below';
  @Input() hasBackdrop = true;
  @Input() matchWidth = false;

  @Output() itemSelected: EventEmitter<MenuPanelItemModel> = new EventEmitter<MenuPanelItemModel>();
  @Output() closed: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('panelTemplate', { static: true }) private panelTemplateRef: TemplateRef<any>;

  private overlayRef: OverlayRef;

  private levels: { items: MenuPanelItemModel[]; title: string }[] = [];

  constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef) { }

  public currentRows: { grouped: boolean; items: MenuPanelItemModel[]; dividerAfter: boolean }[] = [];

  public get currentItems(): MenuPanelItemModel[] {
    return this.levels.length
      ? this.levels[this.levels.length - 1].items
      : this.items;
  }

  public get currentTitle(): string {
    return this.levels.length ? this.levels[this.levels.length - 1].title : null;
  }

  public get isNested(): boolean {
    return this.levels.length > 0;
  }

  public open(sourceElement: HTMLElement): void {
    if (!sourceElement) { return; }
    this.openFrom(sourceElement);
  }

  public openAt(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.openFrom({ x: event.clientX, y: event.clientY });
  }

  public close(): void {
    const wasOpen = !!this.overlayRef;

    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.levels = [];
    this.buildRows();

    if (wasOpen) { this.closed.emit(); }
  }

  public onItemClick(item: MenuPanelItemModel): void {
    if (!item || item.disabled) { return; }

    if (item.contentTpl) { return; }

    if (item.children && item.children.length) {
      this.levels.push({ items: item.children, title: item.label });
      this.buildRows();
      return;
    }

    this.itemSelected.emit(item);

    if (this.closeOnSelect) { this.close(); }
  }

  public goBack(): void {
    this.levels.pop();
    this.buildRows();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.hasOwnProperty('items')) {
      this.buildRows();
    }
  }

  ngOnDestroy(): void {
    this.close();
  }

  private buildRows(): void {
    const rows: { grouped: boolean; items: MenuPanelItemModel[]; dividerAfter: boolean }[] = [];

    this.currentItems.forEach(item => {
      const isGrouped = !!(item && item.group);
      const last = rows[rows.length - 1];

      if (isGrouped && last && last.grouped) {
        last.items.push(item);
        last.dividerAfter = !!(item && item.dividerAfter);
      } else {
        rows.push({ grouped: isGrouped, items: [item], dividerAfter: !!(item && item.dividerAfter) });
      }
    });

    this.currentRows = rows;
  }

  private openFrom(origin: HTMLElement | { x: number; y: number }): void {
    this.close();

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(origin as any)
      .withPositions(this.buildPositions())
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(MENU_VIEWPORT_MARGIN_PX);

    const overlayConfig: OverlayConfig = {
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: this.hasBackdrop,
      backdropClass: 'drop-settings',
    };

    if (this.matchWidth && origin instanceof HTMLElement) {
      overlayConfig.width = origin.getBoundingClientRect().width;
    }

    this.overlayRef = this.overlay.create(overlayConfig);

    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.overlayRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      if (event.key === 'Escape') { this.close(); }
    });

    const portal = new TemplatePortal(this.panelTemplateRef, this.viewContainerRef);
    this.overlayRef.attach(portal);
  }

  private buildPositions(): ConnectedPosition[] {
    const x: 'start' | 'end' = this.xPosition === 'after' ? 'start' : 'end';

    const below: ConnectedPosition = {
      originX: x, originY: 'bottom', overlayX: x, overlayY: 'top', offsetY: MENU_ANCHOR_OFFSET_PX,
    };
    const above: ConnectedPosition = {
      originX: x, originY: 'top', overlayX: x, overlayY: 'bottom', offsetY: -MENU_ANCHOR_OFFSET_PX,
    };

    return this.yPosition === 'above' ? [above, below] : [below, above];
  }
}
